import {
	ReactElement,
	RefObject,
	useMemo,
	useRef,
	useState
} from "react";
import { ContactListItem, ContactSource } from "client/Api/Email/contacts";
import { generateTrackingId } from "client/Misc/utils";
import { getComputedDisplayName } from "../../../common";
import { PersonCard, presenceToAvatarStatusMap } from "client/Features/AddressBook/PersonCard/PersonCard";
import {
	ActionListItem,
	DropOverActionList,
	FontIconName,
	ListItem,
	PersonAvatar
} from "client/Components";
import { useCommunicationActions } from "client/Features/AddressBook/useCommunicationActions";
import { AddressBookOverviewLayoutKind } from "../../OverviewLayoutKindSelector";
import { never } from "client_legacy/ndaw/errors";
import { AddressBookContactSource } from "../../Overview";
import { localize } from "client_legacy/ndaw/culture";

export type ContactItemProps = {
	contact: ContactListItem;
	isActive: boolean;
	onNavigate(): void;
	layoutKind: AddressBookOverviewLayoutKind;
	isResponsive: boolean;
	source: AddressBookContactSource;
};

export const ContactItem = (props: ContactItemProps): ReactElement => {
	const {
		contact,
		isActive,
		onNavigate,
		layoutKind,
		isResponsive,
		source
	} = props;

	switch (layoutKind) {
		case AddressBookOverviewLayoutKind.List:
			return (
				<InternalListItem
					contact={contact}
					isActive={isActive}
					onNavigate={onNavigate}
					isResponsive={isResponsive}
					source={source}
				/>
			);
		case AddressBookOverviewLayoutKind.Grid:
			return (
				<InternalCardItem
					contact={contact}
					isActive={isActive}
					onNavigate={onNavigate}
					source={source}
				/>
			);
		default:
			never(layoutKind);
	}
};

ContactItem.displayName = "ContactItem";

export type ContactItemIdentifier = {
	source: AddressBookContactSource;
	id: string;
};

type ContactCardItemProps = Omit<ContactItemProps, "layoutKind" | "isResponsive">;

const InternalCardItem = (props: ContactCardItemProps): ReactElement => {
	const {
		contact,
		isActive,
		onNavigate,
		source
	} = props;

	const {
		id,
		emailAddress,
		jobTitle,
		company,
		phoneNumber,
		department,
		officeLocation,
		photoUrl,
		presenceStatus,
		skills
	} = contact;

	const {
		computedDisplayName,
		initials
	} = getComputedDisplayName(
		contact,
		undefined,
		source === ContactSource.Gal
	);

	return (
		<PersonCard
			company={company}
			department={department}
			officeLocation={officeLocation}
			jobTitle={jobTitle}
			displayName={computedDisplayName}
			initials={initials}
			contactId={id}
			photoUrl={photoUrl}
			onNavigate={onNavigate}
			presence={presenceStatus}
			emailAddress={emailAddress}
			phoneNumber={phoneNumber}
			skills={skills}
			isActive={isActive}
		/>
	);
};

InternalCardItem.displayName = "ContactItem.Card";

type ContactListItemProps = Omit<ContactItemProps, "layoutKind">;

const InternalListItem = (props: ContactListItemProps): ReactElement => {
	const {
		contact,
		isActive,
		onNavigate,
		isResponsive,
		source
	} = props;

	const {
		id,
		emailAddress,
		jobTitle,
		company,
		phoneNumber,
		department,
		officeLocation,
		photoUrl,
		presenceStatus
	} = contact;

	const {
		computedDisplayName,
		initials
	} = getComputedDisplayName(
		contact,
		undefined,
		source === ContactSource.Gal
	);

	const jobInfo: string = [
		jobTitle,
		department,
		officeLocation,
		company
	]
		.filter(Boolean)
		.join(" | ");

	const trackingId: string = generateTrackingId("contact-list-item", `|${id}`);

	return (
		<ListItem
			trackingId={trackingId}
			isActive={isActive}
		>
			<ListItem.Content
				thumbnail={
					<PersonAvatar
						initials={initials ?? ""}
						imageSrc={photoUrl}
						size="small"
						trackingId="avatar"
						status={{
							kind: "status",
							status: presenceToAvatarStatusMap[presenceStatus]
						}}
					/>
				}
				onClick={onNavigate}
			>
				<ListItem.Title>
					{computedDisplayName}
				</ListItem.Title>
				<ListItem.SubTitle>
					{jobInfo}
				</ListItem.SubTitle>
			</ListItem.Content>

			<ListCommunicationActions
				trackingId={generateTrackingId(trackingId, "__actions")}
				emailAddress={emailAddress}
				phoneNumber={phoneNumber}
				isResponsive={isResponsive}
				computedDisplayName={computedDisplayName}
			/>
		</ListItem>
	);
};

InternalListItem.displayName = "ContactItem.ListItem";

type ListCommunicationActionsProps = {
	trackingId: string;
	emailAddress: string | null;
	phoneNumber: string | null;
	isResponsive: boolean;
	computedDisplayName: string;
};

const ListCommunicationActions = (props: ListCommunicationActionsProps): ReactElement | null => {
	const {
		trackingId,
		emailAddress,
		phoneNumber,
		isResponsive,
		computedDisplayName
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const buttonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const {
		sendEmail,
		makePhoneCall,
		startTeamsChat,
		startTeamsVideoCall
	} = useCommunicationActions({
		emailAddress,
		phoneNumber
	});

	const availableActions = useMemo(
		(): readonly CommunicationAction[] => {
			const result: CommunicationAction[] = [];
			if (sendEmail) {
				result.push({
					invoke: sendEmail,
					icon: "envelope",
					textKey: "_SendEmail",
					trackingId: "send-email"
				});
			}

			if (makePhoneCall) {
				result.push({
					invoke: makePhoneCall,
					icon: "phone",
					textKey: "_StartCall",
					trackingId: "phone-call"
				});
			}

			if (startTeamsVideoCall) {
				result.push({
					invoke: startTeamsVideoCall,
					icon: "video",
					textKey: "_StartVideoChat",
					trackingId: "video-call"
				});
			}

			if (startTeamsChat) {
				result.push({
					invoke: startTeamsChat,
					icon: "comment",
					textKey: "_StartChat",
					trackingId: "chat"
				});
			}

			return result;
		},
		[
			sendEmail,
			makePhoneCall,
			startTeamsChat,
			startTeamsVideoCall
		]
	);

	if (availableActions.length === 0) {
		return null;
	}

	if (isResponsive) {
		return (
			<>
				<ListItem.Action
					onClick={() => setIsOpen(!isOpen)}
					icon="ellipsis"
					ref={buttonRef}
					trackingId="actions"
				/>

				{isOpen && buttonRef.current && (
					<DropOverActionList
						target={buttonRef.current}
						onClose={() => setIsOpen(false)}
						trackingId={generateTrackingId(trackingId, "__drop-over")}
						menuTitle={computedDisplayName}
					>
						{availableActions.map((x, i): ReactElement => (
							<ActionListItem
								key={i}
								icon={x.icon}
								text={localize(x.textKey)}
								onClick={x.invoke}
								trackingId={x.trackingId}
							/>
						))}
					</DropOverActionList>
				)}
			</>
		);
	}

	return (
		<>
			{availableActions.map((x, i): ReactElement => (
				<ListItem.Action
					key={i}
					onClick={x.invoke}
					icon={x.icon}
					trackingId={x.trackingId}
				/>
			))}
		</>
	);
};
ListCommunicationActions.displayName = "ListCommunicationActions";

type CommunicationAction = {
	invoke: () => void;
	icon: FontIconName;
	textKey: LocalizationKey;
	trackingId: string;
};
